// Reuse is the point: without it a swap re-parses every element's html twice.
import { SECTION_PIECE_ATTR, STATIC_ELEMENT_ID_ATTR, STATIC_ELEMENT_INDEX_ATTR } from '../constants';
import { isTitleElement, type TElement, type TSection } from '../model/elements';
import { TITLE_PLACEHOLDER } from '../../tiptap/nodes/title/constants';
import { evictStaleElements, renderedElement } from './staticRenderCache';
import { sanitizeElementHtml } from './sanitizeElementHtml';

// Keyed by id, not by html: a document that repeats a unit would make those elements take each other's nodes.
const placedById = new Map<string, { html: string; nodes: Node[] }>();

// A positional key can go stale, but the entry is used only when the html matches too, so the worst it does is miss.
const keyOf = (element: TElement, absoluteIndex: number): string => element.id ?? `@${absoluteIndex}`;

export const buildStats = { hits: 0, misses: 0, reused: 0 };

/** How many element htmls were parsed. The claim is that a sync with no content change parses none. */
export let parsedElements = 0;

let renderVersion = 0;
export const bumpRenderVersion = () => {
  renderVersion++;
};

/** A commit makes new element strings; without this the renderings keyed by the old ones pile up. */
let evictedFor: readonly TElement[] | null = null;
export const evictOnCommit = (elements: readonly TElement[] | null) => {
  if (!elements || elements === evictedFor) return;
  evictedFor = elements;
  evictStaleElements(elements.map((element) => element.html));
  // Same reason, for the nodes: a key that no longer exists keeps its DOM alive forever.
  const ids = new Set(elements.map((element) => element.id));
  for (const key of placedById.keys()) {
    const positional = key.startsWith('@') ? Number(key.slice(1)) : null;
    if (positional === null ? !ids.has(key) : positional >= elements.length) placedById.delete(key);
  }
};

// Re-stamped on reused nodes too: an edit above an element moves it, and a click sends this number to the model.
const stampIndex = (nodes: Node[], index: number): Node[] => {
  const value = String(index);
  nodes.forEach((node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as Element;
    // On a placed node a write costs a style invalidation, a read costs nothing.
    if (element.getAttribute(STATIC_ELEMENT_INDEX_ATTR) !== value) {
      element.setAttribute(STATIC_ELEMENT_INDEX_ATTR, value);
    }
  });
  return nodes;
};

const nodesForElement = (element: TElement, index: number): Node[] => {
  // A commit rewrites an element's html (renumbering) before its new rendering exists; renderedElement
  // keeps showing the previous rendering until then, which is what stops the page jumping.
  const html = renderedElement(element);
  const key = keyOf(element, index);
  const cached = placedById.get(key);
  if (cached?.html === html) {
    buildStats.hits++;
    return stampIndex(cached.nodes, index);
  }
  buildStats.misses++;

  parsedElements++;
  const nodes = Array.from(sanitizeElementHtml(html).childNodes);
  nodes.forEach((node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    // The element's own id, when it has one - that is what a heading or a reference is looked up by.
    if (element.id) (node as Element).setAttribute(STATIC_ELEMENT_ID_ATTR, element.id);
    // The break keeps this off `p:empty`, whose nbsp would take the pseudo-element the word draws in.
    if (isTitleElement(element) && !node.textContent) {
      (node as Element).setAttribute('data-placeholder', TITLE_PLACEHOLDER);
      node.appendChild(document.createElement('br'));
    }
  });
  stampIndex(nodes, index);
  placedById.set(key, { html, nodes });
  return nodes;
};

/**
 * Kept across syncs: a fresh wrapper each build would make `place` treat the run as new and lay the
 * container out again. Per container, because a section holding the live part has content in both.
 */
const sectionWrappers = new WeakMap<TSection, Map<string, HTMLElement>>();
const sectionWrapper = (section: TSection, block: string): HTMLElement => {
  let perBlock = sectionWrappers.get(section);
  if (!perBlock) {
    perBlock = new Map();
    sectionWrappers.set(section, perBlock);
  }
  const known = perBlock.get(block);
  if (known) return known;
  // Built from the document's own opening tag, so the class and the section's id are whatever it
  // stored rather than a shape guessed here - through the sanitizer, because it IS stored html and
  // everything else drawn here goes through it.
  const wrapper = sanitizeElementHtml(`${section.openTag}</section>`).firstElementChild as HTMLElement;
  perBlock.set(block, wrapper);
  return wrapper;
};

// The wrapper is what makes a landscape section outside the live part draw as a page, not plain blocks.
const buildNodesFresh = (
  elements: readonly TElement[],
  startIndex: number,
  block: string,
  all: readonly TElement[]
): Node[] => {
  const top: Node[] = [];
  let openSection: TSection | undefined;
  let inWrapper: Node[] = [];
  let wrapper: HTMLElement | null = null;
  let runFrom = 0;

  // Read against the whole element list, because this container only ever sees part of the run.
  const closeRun = (runTo: number) => {
    if (wrapper) {
      const first = all[runFrom - 1]?.section !== openSection;
      const last = all[runTo + 1]?.section !== openSection;
      wrapper.setAttribute(SECTION_PIECE_ATTR, first && last ? 'only' : first ? 'first' : last ? 'last' : 'middle');
      place(wrapper, inWrapper);
    }
    wrapper = null;
    inWrapper = [];
  };

  elements.forEach((element, offset) => {
    if (element.section !== openSection) {
      closeRun(startIndex + offset - 1);
      openSection = element.section;
      runFrom = startIndex + offset;
      if (openSection) {
        wrapper = sectionWrapper(openSection, block);
        top.push(wrapper);
      }
    }
    const nodes = nodesForElement(element, startIndex + offset);
    if (wrapper) inWrapper.push(...nodes);
    else top.push(...nodes);
  });
  closeRun(startIndex + elements.length - 1);
  return top;
};

// The built list is a pure function of the element array and the render version, both already tracked.
const lastBuild = new WeakMap<readonly TElement[], { startIndex: number; version: number; nodes: Node[] }>();

export const buildNodes = (
  elements: readonly TElement[],
  startIndex: number,
  block: string,
  all: readonly TElement[]
): Node[] => {
  const previous = lastBuild.get(elements);
  if (previous && previous.startIndex === startIndex && previous.version === renderVersion) {
    buildStats.reused += elements.length;
    return previous.nodes;
  }
  const nodes = buildNodesFresh(elements, startIndex, block, all);
  lastBuild.set(elements, { startIndex, version: renderVersion, nodes });
  return nodes;
};

// Not `replaceChildren`: it detaches every child, so nothing is reused and the whole container lays out again.
export const place = (container: HTMLElement, nodes: Node[]) => {
  /**
   * Take the departing nodes out FIRST.
   *
   * Walking and inserting alone falls apart the moment an element disappears from the middle - which
   * is every part swap, where the live part leaves the static half. The cursor stops matching at that
   * point and every node after it gets `insertBefore`d, so removing a few elements moves every one
   * below them.
   *
   * Removing them first leaves the rest in the order they are already in, so the walk below only has
   * to insert what is genuinely new.
   */
  const wanted = new Set<Node>(nodes);
  for (const child of Array.from(container.childNodes)) {
    if (!wanted.has(child)) container.removeChild(child);
  }

  let cursor: Node | null = container.firstChild;
  nodes.forEach((node) => {
    if (cursor === node) {
      cursor = cursor.nextSibling;
      return;
    }
    container.insertBefore(node, cursor);
  });
  while (cursor) {
    const next = cursor.nextSibling;
    container.removeChild(cursor);
    cursor = next;
  }
};
